import { useEffect, useRef, useState } from 'react';

/**
 * A lightweight location picker built on Leaflet + OpenStreetMap. Leaflet is
 * loaded from a CDN at runtime (no npm dependency, to respect the frozen
 * lockfile). Click the map or drag the pin to set lat/lng; an optional search
 * box geocodes a place name via Nominatim.
 */
const LEAFLET_VER = '1.9.4';
let leafletPromise: Promise<unknown> | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadLeaflet(): Promise<any> {
  const w = window as unknown as { L?: unknown };
  if (w.L) return Promise.resolve(w.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.css`;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.js`;
    script.async = true;
    script.onload = () => resolve((window as unknown as { L: unknown }).L);
    script.onerror = () => reject(new Error('leaflet_load_failed'));
    document.head.appendChild(script);
  });
  return leafletPromise;
}

export interface LatLng { lat: number; lng: number }

export interface MapPickerProps {
  value: LatLng | null;
  onChange: (v: LatLng) => void;
  height?: string;
  /** Default centre when nothing is picked yet (e.g. the tenant's city). */
  defaultCenter?: LatLng;
  searchPlaceholder?: string;
  unavailableLabel?: string;
}

const round = (n: number): number => Math.round(n * 1e6) / 1e6;

export function MapPicker({ value, onChange, height = '280px', defaultCenter, searchPlaceholder, unavailableLabel }: MapPickerProps): JSX.Element {
  const elRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markerRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);

  // Init the map once.
  useEffect(() => {
    let active = true;
    void loadLeaflet().then((L) => {
      if (!active || !elRef.current || mapRef.current) return;
      // A themed SVG pin (divIcon) — avoids the default-marker PNGs (which often
      // fail to load) and tints the pin with the app's brand colour (--primary,
      // defined on :root so it resolves inside portaled dialogs too).
      const themedIcon = L.divIcon({
        className: 'erp-map-pin',
        html:
          '<svg width="30" height="42" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg" style="color: var(--primary); filter: drop-shadow(0 2px 3px rgba(0,0,0,.35))">' +
          '<path fill="currentColor" stroke="white" stroke-width="1.5" d="M12 .5C5.65.5.5 5.65.5 12c0 8.4 11.5 23.5 11.5 23.5S23.5 20.4 23.5 12C23.5 5.65 18.35.5 12 .5z"/>' +
          '<circle cx="12" cy="12" r="4.3" fill="white"/></svg>',
        iconSize: [30, 42],
        iconAnchor: [15, 42],
        popupAnchor: [0, -38],
      });
      const start = value ?? defaultCenter ?? { lat: 33.3152, lng: 44.3661 };
      const map = L.map(elRef.current).setView([start.lat, start.lng], value ? 14 : 6);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);
      const marker = L.marker([start.lat, start.lng], { draggable: true, icon: themedIcon }).addTo(map);
      marker.on('dragend', () => {
        const p = marker.getLatLng();
        onChangeRef.current({ lat: round(p.lat), lng: round(p.lng) });
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.on('click', (e: any) => {
        marker.setLatLng(e.latlng);
        onChangeRef.current({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) });
      });
      mapRef.current = map;
      markerRef.current = marker;
      setTimeout(() => map.invalidateSize(), 120);
    }).catch(() => { if (active) setFailed(true); });
    return () => {
      active = false;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter when the value changes from outside (search / clear).
  useEffect(() => {
    if (mapRef.current && markerRef.current && value) {
      markerRef.current.setLatLng([value.lat, value.lng]);
      mapRef.current.setView([value.lat, value.lng], Math.max(mapRef.current.getZoom() ?? 13, 13));
    }
    // Depend on the primitive coords, not the object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng]);

  const search = async (): Promise<void> => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`);
      const hits = (await r.json()) as Array<{ lat: string; lon: string }>;
      if (hits[0]) onChange({ lat: round(Number(hits[0].lat)), lng: round(Number(hits[0].lon)) });
    } catch { /* ignore — manual click still works */ } finally { setSearching(false); }
  };

  if (failed) {
    return (
      <div className="flex items-center justify-center rounded-md border border-dashed border-border bg-bg-subtle text-sm text-fg-muted" style={{ height }}>
        {unavailableLabel ?? 'Map unavailable (no internet).'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }}
          placeholder={searchPlaceholder ?? 'Search a place…'}
          className="h-9 flex-1 rounded-md border border-border bg-bg px-3 text-sm text-fg outline-none focus:border-brand"
        />
        <button type="button" onClick={() => void search()} disabled={searching || !query.trim()} className="h-9 rounded-md border border-border px-3 text-sm font-medium text-fg-muted hover:bg-bg-subtle disabled:opacity-50">
          {searching ? '…' : '🔍'}
        </button>
      </div>
      <div ref={elRef} style={{ height }} className="z-0 overflow-hidden rounded-md border border-border" />
    </div>
  );
}
